import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  output,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy } from '@ngneat/until-destroy';

import { BlazeAccountFacade } from '@blaze-shared/app-state-data-access';
import { urlUtils } from '@blaze-shared/platform-util';
import { FileResult, UploaderFacade, UploaderOptions } from '@blaze-shared/uploader';

import {
  CellValueUpdateModelForImage,
  type InlineEditorConfig,
  type InlineEditorLayoutMode,
} from '../base-editor/inline-editor.types';

@UntilDestroy()
@Component({
  selector: 'be-image-menu',
  templateUrl: './image-menu.component.html',
  styleUrls: ['./image-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableImageMenuComponent implements AfterViewInit {
  private readonly blazeAccountFacade = inject(BlazeAccountFacade);
  private readonly uploader = inject(UploaderFacade);

  valueChange = output<unknown>();

  @Input() config?: InlineEditorConfig;
  @Input() layoutMode?: InlineEditorLayoutMode;
  @Input() value: string | number | string[] | null | undefined;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger | undefined;

  acceptedExtensions = '*.*';
  projectId = '';

  readonly getFileUrlHref = urlUtils.getFileUrlHref;

  get isInputLike(): boolean {
    return this.layoutMode === 'input-like';
  }

  get detailDate(): CellValueUpdateModelForImage | undefined {
    return this.config?.detailData as CellValueUpdateModelForImage;
  }

  get detailValue(): string | undefined {
    return this.layoutMode === 'input-like' ? (this.value as string) : this.detailDate?.imageUrl;
  }

  get isAddAction(): boolean {
    return this.detailDate?.isAddFileAction ?? false;
  }

  get isDownloadAction(): boolean {
    return this.detailDate?.isDownloadFileAction ?? false;
  }

  get isRemoveFileAction(): boolean {
    return this.detailDate?.isRemoveFileAction ?? false;
  }

  get isMultiple(): boolean {
    return this.config?.columnSchema?.multipleValues || false;
  }

  ngAfterViewInit(): void {
    if (this.isAddAction) {
      setTimeout(() => {
        this.startAddFileProcess();
      });
    } else if (this.isRemoveFileAction) {
      setTimeout(() => {
        this.fileRemove();
      });
    } else if (this.layoutMode !== 'input-like') {
      this.menuTrigger?.openMenu();
    }
  }

  fileRemove(event?: Event, file?: string): void {
    if (this.layoutMode === 'input-like') {
      if (this.isMultiple) {
        this.value = (this.value as string[]).filter((value, index) =>
          file ? value !== file : index !== 0,
        );
        this.valueChange.emit(this.value);
      } else {
        this.valueChange.emit(null);
      }
    } else {
      this.valueChange.emit({ url: this.detailValue, isRemoveAction: true });
    }
    event?.preventDefault();
  }

  fileSelected(fileInput: EventTarget | null): void {
    const fileInputElement = fileInput as HTMLInputElement;
    const files = Array.from(fileInputElement?.files ?? []);
    fileInputElement.value = '';

    if (!files || files.length === 0) {
      return;
    }
    this.upload(files);
  }

  externalClickAction(
    event: FileResult | ({ selected: string } & CellValueUpdateModelForImage),
  ): void {
    if ((event as CellValueUpdateModelForImage).isAddFileAction) {
      this.startAddFileProcess();
    } else if ((event as CellValueUpdateModelForImage).imageUrl) {
      this.menuTrigger?.openMenu();
    }
  }

  closeEditor(): void {
    this.valueChange.emit(this.value);
  }

  private upload(preselectFiles?: File[]): void {
    const options: UploaderOptions = {
      accountId: this.blazeAccountFacade.getAccountId(),
      preselectFiles,
      acceptedFileTypes: [this.acceptedExtensions],
      isMultipleSelection: this.isMultiple || false,
    };

    this.uploader.upload(options).subscribe((results) => {
      if (!results) {
        return;
      }

      let selected: string | string[] = results.map((v) => v?.url).filter(Boolean) as string[];
      if (!this.isMultiple) {
        selected = selected[0];
      }

      if (this.layoutMode === 'input-like') {
        if (this.isMultiple) {
          this.value = [...((this.value as string[]) || []), ...selected];
        } else {
          this.value = selected;
        }
        this.valueChange.emit(this.value);
      } else {
        this.valueChange.emit({ selected, isAddAction: this.isAddAction });
      }
    });
  }

  private startAddFileProcess(): void {
    this.fileInput.nativeElement.click();
  }
}
